import { Platform } from '@angular/cdk/platform';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

declare const window: any;

/**
 * Service to detect the current platform by comparing the userAgent strings and
 * checking browser-specific global properties.
 */
@Injectable({ providedIn: 'root' })
export class RtPlatform extends Platform {
  public stickySupported: boolean = this.detectStickySupport();

  /** Layout Engines */
  public readonly OLDER_IE: boolean = /(MSIE )/i.test(navigator.userAgent);

  /** Browsers and Platform Types */
  public readonly WINDOWS: boolean = /(Win)/.test(navigator.platform);
  public readonly OSX: boolean = /(Mac)/.test(navigator.platform);
  public readonly LINUX: boolean = /(Linux)/.test(navigator.platform);

  public readonly CHROME: boolean = !!window.chrome && /(chrome)/i.test(navigator.userAgent);

  public readonly OPERA: boolean = /Opera/.test(navigator.userAgent);

  public readonly IS_MOBILE: boolean = this.isMobile(
    navigator.userAgent || navigator.vendor || window.opera,
  );

  public readonly IS_DESKTOP: boolean = !this.IS_MOBILE;

  public readonly IS_MS: boolean = this.EDGE || this.TRIDENT || this.OLDER_IE;

  public scrollbarWidth: number = 0;

  public currentPlatform: { classNames: string[]; toString: () => string };

  public get ua(): string {
    return navigator.userAgent;
  }

  constructor() {
    super(inject(PLATFORM_ID));
    this.scrollbarWidth = this.getScrollbarWidth();
    this.currentPlatform = this.resolveEnvironmentClassname();
  }

  public isStickySupported(): boolean {
    return this.stickySupported;
  }

  public getScrollbarWidth(): number {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.cssText =
      'width: 100px;height: 100px;overflow: scroll;position: absolute;top: -9999px;';
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
  }

  // eslint-disable-next-line max-lines-per-function
  public resolveEnvironmentClassname(): { classNames: string[]; toString: () => string } {
    const classNames = [];

    if (this.IS_DESKTOP) {
      classNames.push('desktop');
    }

    if (this.IS_MOBILE) {
      classNames.push('mobile');
    }

    if (this.WINDOWS) {
      classNames.push('windows');
    }

    if (this.OSX) {
      classNames.push('osx');
    }

    if (this.LINUX) {
      classNames.push('linux');
    }

    if (this.IOS) {
      classNames.push('ios');
    }

    if (this.ANDROID) {
      classNames.push('android');
    }

    if (this.TRIDENT) {
      classNames.push('ie');
    }

    return {
      classNames,
      toString(): string {
        return this.classNames.join(' ');
      },
    };
  }

  private detectStickySupport(): boolean {
    const prefixes = ['', '-webkit-', '-moz-', '-ms-'],
      block = document.createElement('div');

    for (let i = prefixes.length - 1; i >= 0; i--) {
      try {
        block.style.position = prefixes[i] + 'sticky';
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      } catch (e) {}
      if (block.style.position !== '') {
        return true;
      }
    }

    return false;
  }

  private isMobile(useragent: string): boolean {
    // credits: http://detectmobilebrowsers.com/mobile
    return (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        useragent,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        useragent.substr(0, 4),
      )
    );
  }
}
